"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { capitalCase } from "change-case";
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import Flag from "react-world-flags";
import countriesData from "world-countries";

import MiniEditor from "@/components/common/editor/mini-editor";
import { Button } from "@/components/new/button";
import { Checkbox } from "@/components/new/checkbox";
import { FileUpload } from "@/components/new/file-upload";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/new/form";
import { Input } from "@/components/new/input";
import { RadioGroup, RadioGroupItem } from "@/components/new/radio-group";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/new/select";
import { BASE_URL } from "@/lib/constants";
import { getTimeZones } from "@/lib/utils";
import { CommunityEventSchema, EventLevel, EventTypeSchema, TEventFormData } from "@/types/events";
import { useParams, usePathname } from "next/navigation";
import toast from "react-hot-toast";
import { v4 as uuid } from "uuid";
import CustomTagSelect from "./custom-tag-select";

interface CommunityEventFormProps {
	onSubmit: (data: TEventFormData) => Promise<void>;
	initialData?: TEventFormData;
}

const getEventTypes = () => {
	return Object.values(EventTypeSchema.enum).map((e) => ({
		value: e,
		label: capitalCase(e.toString()),
	}));
};

const getLevelData = () => {
	return Object.values(EventLevel.enum).map((e) => ({
		value: e,
		label: capitalCase(e.toString()),
	}));
};

const formatDateTime = (value: string): string => {
	if (!value) return "";
	const date = new Date(value);
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	const hours = String(date.getHours()).padStart(2, "0");
	const minutes = String(date.getMinutes()).padStart(2, "0");
	return `${year}-${month}-${day}T${hours}:${minutes}`;
};

export function CommunityEventForm({ onSubmit, initialData }: CommunityEventFormProps) {
	const params = useParams();
	const communitySlug = params["community-slug"] as string;
	const [timeZones, setTimeZones] = useState<
		{ value: string; label: string; isUserTimezone: boolean }[]
	>([]);
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const editorRef = useRef<any>(null);

	useEffect(() => {
		const tzList = getTimeZones();
		setTimeZones(tzList);
	}, []);

	const pathname = usePathname();
	const isGlobalEventsPage = pathname === "/events";

	const form = useForm<TEventFormData>({
		resolver: zodResolver(CommunityEventSchema),
		defaultValues: {
			communitySlug: communitySlug || undefined,
			tags: [],
			timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			isPrivate: isGlobalEventsPage ? false : true,
			location: "ONLINE",
			type: "SUMMIT",
			level: "LOCAL",
			tagline: "",
			description: "",
			rawEventData: {
				isPremium: false,
				registrationType: "EXTERNAL",
			},
			...initialData,
		},
	});

	const [isLoading, setIsLoading] = useState(false);
	const location = form.watch("location");
	const level = form.watch("level");
	const registrationType = form.watch("rawEventData.registrationType");

	const uploadToS3 = async (file: File) => {
		const key = `communities/${communitySlug}/${uuid()}.${file.name.split(".").pop()}`;
		const apiURL = new URL(`${BASE_URL}/api/presign`);
		apiURL.searchParams.set("key", key);

		try {
			const presignUrlResponse = await fetch(apiURL);
			const { objectPath, preSignedUrl } = await presignUrlResponse.json();

			await fetch(preSignedUrl, {
				method: "PUT",
				body: file,
			});

			form.setValue("coverImage", objectPath);
			return objectPath;
		} catch (error) {
			toast.error("Something went wrong during file upload");
			console.log(error);
		}
	};

	async function handleSubmit(data: TEventFormData) {
		try {
			setIsLoading(true);
			if (Object.keys(form.formState.errors).length > 0) {
				toast.error("Please fix form errors before submitting");
				return;
			}
			await onSubmit(data);
		} catch (error) {
			console.error("Error submitting form:", error);
			toast.error("Failed to submit event");
		} finally {
			setIsLoading(false);
		}
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="title"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Event Title</FormLabel>
							<FormControl>
								<Input placeholder="Enter event title" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="organizerName"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Organizer Name</FormLabel>
							<FormControl>
								<Input placeholder="Enter organizer name" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="description"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Description</FormLabel>
							<FormControl>
								<MiniEditor
									content={field.value || ""}
									editorRef={editorRef}
									placeholder="Write event description..."
									handleOnUpdate={({ content }) => field.onChange(content)}
									classNames={{
										container: "mt-2 border border-gray-300 rounded-lg p-3",
										editor: "min-h-[150px] text-gray-700 placeholder-gray-500 px-2 py-1",
									}}
									disableOptions={[
										"Heading 1",
										"Heading 2",
										"Heading 3",
										"Blockquote",
										"Code",
										"Image",
										"Divider",
										"Undo",
										"Redo",
										"YouTube",
										"Attach",
										"Line Break",
										"Paragraph",
									]}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="tagline"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Tagline</FormLabel>
							<FormControl>
								<Input placeholder="Add a tagline" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<FormField
					control={form.control}
					name="timezone"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Event Time Zone</FormLabel>
							<Select onValueChange={field.onChange} defaultValue={field.value}>
								<FormControl>
									<SelectTrigger>
										<SelectValue placeholder="Select event time zone" />
									</SelectTrigger>
								</FormControl>
								<SelectContent className="max-h-60 overflow-y-auto">
									{timeZones.map((tz) => (
										<SelectItem
											key={tz.value}
											value={tz.value}
											className={tz.isUserTimezone ? "font-bold" : ""}
										>
											{tz.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<FormDescription>
								Select the time zone where the event will take place.
							</FormDescription>
							<FormMessage />
						</FormItem>
					)}
				/>

				<div className="grid grid-cols-2 gap-2">
					<FormField
						control={form.control}
						name="dateTime"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>Start Date & Time</FormLabel>
								<FormControl>
									<Input
										type="datetime-local"
										placeholder="Select start date and time"
										value={field.value || ""}
										onChange={(e) => {
											const { value } = e.target;
											field.onChange(value ? formatDateTime(value) : ""); // Format to YYYY-MM-DDTHH:mm
										}}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="endDateTime"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>End Date & Time</FormLabel>
								<FormControl>
									<Input
										type="datetime-local"
										placeholder="Select end date and time"
										value={field.value || ""}
										onChange={(e) => {
											const { value } = e.target;
											field.onChange(value ? formatDateTime(value) : ""); // Format to YYYY-MM-DDTHH:mm
										}}
									/>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="grid grid-cols-2 gap-2">
					<FormField
						control={form.control}
						name="type"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>Event Type</FormLabel>
								<Select onValueChange={field.onChange} defaultValue={field.value}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select event type" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{getEventTypes().map((eventType) => (
											<SelectItem key={eventType.value} value={eventType.value.toString()}>
												{eventType.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="level"
						render={({ field }) => (
							<FormItem className="flex flex-col">
								<FormLabel>Event Level</FormLabel>
								<Select onValueChange={field.onChange} defaultValue={field.value}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select event level" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{getLevelData().map((eventLevel) => (
											<SelectItem key={eventLevel.value} value={eventLevel.value.toString()}>
												{eventLevel.label}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="grid grid-cols-2 gap-2">
					{level === "LOCAL" && (
						<FormField
							control={form.control}
							name="rawEventData.address.city"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>City</FormLabel>
									<FormControl>
										<Input placeholder="Enter city" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}

					{(level === "LOCAL" || level === "NATIONAL") && (
						<FormField
							control={form.control}
							name="rawEventData.address.state"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>State</FormLabel>
									<FormControl>
										<Input placeholder="Enter state" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				<div className="grid grid-cols-2 gap-2">
					<FormField
						control={form.control}
						name="location"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Location Type</FormLabel>
								<Select onValueChange={field.onChange} defaultValue={field.value}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select location type" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										<SelectItem value="ONLINE">Online</SelectItem>
										<SelectItem value="OFFLINE">Offline</SelectItem>
										<SelectItem value="HYBRID">Hybrid</SelectItem>
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>

					<FormField
						control={form.control}
						name="rawEventData.address.country"
						render={({ field }) => (
							<FormItem>
								<FormLabel>Country</FormLabel>
								<Select onValueChange={field.onChange} defaultValue={field.value}>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder="Select country" />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{countriesData.map((country) => (
											<SelectItem key={country.cca2} value={country.name.common}>
												<div className="flex items-center gap-2">
													<Flag code={country.cca2} className="h-5 w-5" />
													<span>{country.name.common}</span>
												</div>
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				<div className="w-full">
					{(location === "ONLINE" || location === "HYBRID") &&
						(registrationType === "PTB" || registrationType === "NONE") && (
							<FormField
								control={form.control}
								name="link"
								render={({ field }) => (
									<FormItem className="col-span-2">
										<FormLabel>Meeting Link</FormLabel>
										<FormControl>
											<Input placeholder="Enter meeting link" {...field} />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						)}
				</div>

				<div className="w-full">
					{(location === "OFFLINE" || location === "HYBRID") && (
						<FormField
							control={form.control}
							name="rawEventData.address.fullAddress"
							render={({ field }) => (
								<FormItem className="col-span-2">
									<FormLabel>Full Address</FormLabel>
									<FormControl>
										<Input placeholder="Enter full address" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					)}
				</div>

				{(location === "OFFLINE" || location === "HYBRID") && (
					<div className="grid grid-cols-2 gap-2">
						<FormField
							control={form.control}
							name="rawEventData.address.venueName"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>Venue Name</FormLabel>
									<FormControl>
										<Input placeholder="Enter venue name" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name="rawEventData.address.gmapLink"
							render={({ field }) => (
								<FormItem className="flex flex-col">
									<FormLabel>Google Map Link</FormLabel>
									<FormControl>
										<Input placeholder="Enter Google Map link" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}

				<CustomTagSelect name="tags" setValue={form.setValue} getValues={form.getValues} />

				<FormField
					control={form.control}
					name="isPrivate"
					render={({ field }) => (
						<FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
							<FormControl>
								<Checkbox
									checked={!field.value}
									onCheckedChange={(checked) => field.onChange(!checked)}
									disabled={isGlobalEventsPage}
								/>
							</FormControl>
							<div className="space-y-1 leading-none">
								<FormLabel>Make this a public event</FormLabel>
							</div>
						</FormItem>
					)}
				/>

				<div className="mt-6 space-y-2">
					<h3 className="mb-3 text-lg font-medium">Registration Options</h3>
					<FormField
						control={form.control}
						name="rawEventData.registrationType"
						render={({ field }) => (
							<FormItem className="space-y-3">
								<FormControl>
									<RadioGroup
										onValueChange={field.onChange}
										defaultValue={registrationType}
										className="flex flex-col space-y-1"
									>
										<FormItem className="flex items-center space-x-3 space-y-0">
											<FormControl>
												<RadioGroupItem value="PTB" />
											</FormControl>
											<FormLabel className="font-normal">Use ProptechBuzz registration</FormLabel>
										</FormItem>
										<FormItem className="flex items-center space-x-3 space-y-0">
											<FormControl>
												<RadioGroupItem value="EXTERNAL" />
											</FormControl>
											<FormLabel className="font-normal">Use external registration link</FormLabel>
										</FormItem>
										<FormItem className="flex items-center space-x-3 space-y-0">
											<FormControl>
												<RadioGroupItem value="NONE" />
											</FormControl>
											<FormLabel className="font-normal">No registration required</FormLabel>
										</FormItem>
									</RadioGroup>
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				</div>

				{registrationType === "PTB" && (
					<div className="mt-4 space-y-4 rounded-md border p-4">
						<FormField
							control={form.control}
							name="rawEventData.landingPage.title"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Landing Page Title</FormLabel>
									<FormControl>
										<Input placeholder="Enter landing page title" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="rawEventData.landingPage.subTitle"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Subtitle</FormLabel>
									<FormControl>
										<Input placeholder="Enter subtitle" {...field} />
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="rawEventData.landingPage.description"
							render={({ field }) => (
								<FormItem>
									<FormLabel>Landing Page Description</FormLabel>
									<FormControl>
										<textarea
											className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
											placeholder="Enter landing page description"
											{...field}
										/>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}

				{registrationType === "EXTERNAL" && (
					<div className="mt-4 space-y-4 rounded-md border p-4">
						<FormField
							control={form.control}
							name="externalLink"
							render={({ field }) => (
								<FormItem>
									<FormLabel>External Registration Link</FormLabel>
									<FormControl>
										<Input placeholder="Enter external registration URL" {...field} />
									</FormControl>
									<FormDescription>
										Provide the full URL where attendees can register for this event
									</FormDescription>
									<FormMessage />
								</FormItem>
							)}
						/>
					</div>
				)}

				<FormField
					control={form.control}
					name="coverImage"
					render={({ field }) => (
						<FormItem className="col-span-2">
							<FormLabel>Cover Image</FormLabel>
							<FormControl>
								<FileUpload onSelect={uploadToS3} {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				<Button type="submit" disabled={isLoading}>
					{isLoading ? "Creating..." : initialData ? "Update Event" : "Create Event"}
				</Button>
			</form>
		</Form>
	);
}
